import { FormControl, Stack, Select, MenuItem, InputLabel, Accordion, AccordionSummary, AccordionDetails, OutlinedInput} from '@mui/material'
import React, { useContext, useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ParentScreenCtx } from '../../../../pages/workbench_build/WorkbenchBuild';
import { useDispatch, useSelector } from 'react-redux';
import GetDataCheckbox from './GetDataCheckbox';
import { encodingConstant, SERVICE_SUPPORTED_GETDATA_ADAPTER_TYPES } from '../../../../constants/constantList';
import { cloneDeep, debounce, isArray } from 'lodash';
import { treeActions } from '../../../../redux/slices/treeSlice';
import { eachDeep } from "deepdash-es/standalone";
import WorkflowConnectionPanel from '../Common/WorkflowConnectionPanel';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SectionHeader } from '../../Forms/SectionHeader';
import { api } from '../../../../redux/api';
import { validateWorkflowItemName } from '../../../../utils/utilFunctions';
import ErrorModal from '../../Forms/FieldDataValidation/ErrorModal';

function GetDataProperties() {
  const { t } = useTranslation();
  const parentScreen = useContext(ParentScreenCtx);
  const dispatch = useDispatch();
  const { account } = useSelector(state => state.queryParams)
  const { rightTree, leftTree, templateChanged  } = useSelector(state => state.treeData[parentScreen]);
  const currentTab = rightTree.currentTab;
  const { translatedData } = rightTree.tree.body
  const selectedNode = rightTree.tree[currentTab].selectedNode;
  const { templateHeader } = useSelector(state => state.treeData.workflow.rightTree.tree)
  const wfItemId = selectedNode?.ID;
  const { ADAPTER_TYPE } = selectedNode?.INBOUND
  const [name, setName] = useState('');
  const [encoding, setEncoding] = useState('');
  const [connectionInfo, setConnectionInfo] = useState({});
  const [runOnServer, setRunOnServer] = useState('LBL_RUN_ON_ALL_ACTIVE_SERVER');
  const [runOnServerDropdownItems, setRunOnServerDropdownItems] = useState([])
  const [errorModal, setErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const encodingConstants = ADAPTER_TYPE === "BridgeGateHTTP" ? {...encodingConstant} : {...encodingConstant, ENCODING_BYTES:'ENCODING_BYTES'};


  const encodingOptions = [...Object.keys(encodingConstants)].map((e, i) => {
    return (
      <MenuItem value={encodingConstants[e]} key={i}>
        {t(encodingConstants[e])}
      </MenuItem>
    );
  });
  const runOnServerOptions = runOnServerDropdownItems.map((e,i) => {
    return (
      <MenuItem value={e.value} key={`${e.value}-i`}>
        {e.text}
      </MenuItem>
    )
  })
  const adapterTypeHandlers = {
    BridgeGateSocket: (node) => ({ SYNCHRONOUS: node?.SYNCHRONOUS }),
    FILE: (node) => ({ RUN_ON_SERVER: node?.RUN_ON_SERVER }),
    JMS: (node) => ({ SYNCHRONOUS: node?.SYNCHRONOUS })
    // Add more adapter types and their logic here
  };

  const getFirstGetDataNode = () => {
    const oneDeepArray = translatedData?.[0]?.children || [];
    return oneDeepArray.find((node) => node?.ICON_DISPLAY === "GET_DATA");
  };

  const hasAnyServiceSupportedGetData = (treeData) => {
    const stack = isArray(treeData) ? [...treeData] : [];
    while (stack.length) {
      const node = stack.pop();
      if (!node) continue;
      if (node?.ICON_DISPLAY === "GET_DATA") {
        const adapterType = node?.INBOUND?.ADAPTER_TYPE;
        if (SERVICE_SUPPORTED_GETDATA_ADAPTER_TYPES.includes(adapterType)) return true;
      }
      if (isArray(node?.children)) stack.push(...node.children);
    }
    return false;
  };
  useEffect(() => {
    if(selectedNode){
      const encoding = selectedNode?.INBOUND?.ENCODING  || 'DEFAULT_ENCODING';
      const extraConnectionInfo = adapterTypeHandlers[selectedNode?.INBOUND?.ADAPTER_TYPE]?.(selectedNode) || {};

      setName(selectedNode?.NAME);
      setEncoding(encoding);
      selectedNode?.connDetailUpdateFlag !== "predefined" && setConnectionInfo({...selectedNode?.INBOUND, ...extraConnectionInfo});
      setRunOnServer(selectedNode?.RUN_ON_SERVER || t('LBL_RUN_ON_ALL_ACTIVE_SERVER'))
    }
    
  },[selectedNode?.key, selectedNode?.INBOUND?.OVERRIDE, selectedNode?.INBOUND?.ENCRYPTION_TYPE, selectedNode?.connDetailUpdateFlag]);


  useEffect(() => {
    if (templateChanged) {
			dispatch(
				treeActions.setLeftTreeContextActionValue({
					screen: "workflow",
					data: {
						name: leftTree.tree.body.selectedNode.title,
						workflowGroup: leftTree.tree.body.selectedNode.workflowGroup,
					},
				})
			);
		}
  },[selectedNode?.key])

  useEffect(()=>{
    const adapterType = selectedNode?.INBOUND?.ADAPTER_TYPE;
    const extraConnectionInfo = adapterTypeHandlers[selectedNode?.INBOUND?.ADAPTER_TYPE]?.(selectedNode) || {};

    setConnectionInfo({...selectedNode?.INBOUND, ...extraConnectionInfo})
    if(['FILE'].includes(adapterType)){
      const options = [{text:t('LBL_RUN_ON_ALL_ACTIVE_SERVER'),value:'LBL_RUN_ON_ALL_ACTIVE_SERVER'},{text:t('LBL_RUN_ON_PRIMARY'),value:'LBL_RUN_ON_PRIMARY'}]
      dispatch(api.endpoints.getBridgegateConfig.initiate({
        account
      })).then((response) => {
        if(response && response?.data.current_state === 'SUCCESS'){
          const list = response?.data?.data?.BRIDGEGATE_CLUSTER?.COMMON_NAME_LIST?.split(',') || [];
          const formattedList = list.map((item) => ({
            value: item,
            text: item,
          }));
          setRunOnServerDropdownItems([...options,...formattedList])
        } else {
          setRunOnServerDropdownItems(options)
        }
      })
    }
  },[selectedNode?.INBOUND?.ADAPTER_TYPE])
  
  // update SERVICE NAME in template header when 1st Get data name changed
  const updateTemplateHeaderIfFirstGetData = (value) => {
    const firstGetDataNode = translatedData?.[0]?.children?.find(
      (node) => node.ICON_DISPLAY === "GET_DATA"
    );
  
    if (selectedNode?.key === firstGetDataNode?.key) {
      const updatedHeader = {
        ...cloneDeep(templateHeader),
        SERVICE: { ...templateHeader.SERVICE, NAME: value },
      };
  
      dispatch(treeActions.setRightTreeTemplateHeader({
        value: updatedHeader,
        screen: parentScreen,
      }));
    }
  };

  const deleteServiceFromTemplateHeader = () => {
    if (templateHeader?.hasOwnProperty('SERVICE')) {
      const updatedHeader = cloneDeep(templateHeader);
      delete updatedHeader.SERVICE;
  
      dispatch(treeActions.setRightTreeTemplateHeader({
        value: updatedHeader,
        screen: parentScreen,
      }));
    }
  };
  
  const handleConnectionDetailsChange = (value) => {
    if (value !== null) {
      // Check if 'remove' exists in the value
      if (value.remove) {
        debounceChangeHandler(null, null, null, value.remove);
        return; // Exit early if 'remove' is handled
      }
      // Check if 'add' exists in the value
      if(value.add) {
        debounceChangeHandler(null,null,value.add,null);
        return;
      }
      // To handle updates
      const entries = Object.entries(value);
      const [key, val] = entries[entries.length - 1] || [];
      submitChange(key,val)
        // debounceChangeHandler(key, val);
    }
  }
 
  /**
   * Function to submit a change to a selected node in the tree structure.
   * Updates the node's properties based on the provided key-value pair and dispatches actions to update the state.
   **/
  const submitChange = (key, value, bulk, removeValues) => {
    if (!key && !removeValues && !bulk) return;
    let shouldDeleteServiceHeader = false;
    const firstGetDataNode = getFirstGetDataNode();
    const isSelectedFirstGetData = selectedNode?.key && firstGetDataNode?.key && selectedNode.key === firstGetDataNode.key;
    // Clone and deeply iterate through the tree data to update the relevant node.
    const updatedData = eachDeep(cloneDeep(translatedData), (obj) => {
      if (obj?.key !== selectedNode?.key) return;

      if (key === "NAME") {
        obj.title = value;
        obj[key] = value;
        if(templateHeader?.hasOwnProperty('SERVICE')){
          updateTemplateHeaderIfFirstGetData(value)
        }
      }
      else if (key === "ENCODING" || key === 'OVERRIDE') {
        if(key === 'ENCODING' && value === 'DEFAULT_ENCODING'){
          delete obj.INBOUND[key]
        }
       else obj.INBOUND[key] = value;
      }
    // While changing ADAPTER_TYPE, remove keys that are not relevant to the selected adapter
      else if (key === 'ADAPTER_TYPE') {
        // Store the previous adapter type before changing
        const prevAdapterType = obj.INBOUND[key];
    
        // Update ADAPTER_TYPE value
        obj.INBOUND[key] = value;
    
        // Get relevant keys for both previous and new adapter type
        const prevKeys = adapterBasedKeysMap[prevAdapterType] || [];
        const newKeys = adapterBasedKeysMap[value] || [];
    
        if (!newKeys.length) {
            console.warn(`Adapter type "${value}" is not mapped. No changes made to INBOUND.`);
            return;
        }
    
        // Define a set of properties that should always be retained
        const alwaysRetainKeys = new Set(["ADAPTER_TYPE","ENCODING","DONT_RETAIN_PLAIN_TEXT","ENCRYPTION_TYPE","ENCRYPTION_PARAM1","RETRY_COUNT", "TIME_BEFORE_RETRY", "SYNCHRONOUS"]); 
    
        // Identify common keys between old and new adapter type
        const commonKeys = prevKeys.filter((key) => newKeys.includes(key));
    
        // Identify which common keys should be deleted (i.e., they exist in both but should reset)
        const keysToDelete = commonKeys.filter((key) => !alwaysRetainKeys.has(key));
    
        // Delete properties not in the new adapter type or those marked for deletion
        Object.keys(obj.INBOUND).forEach((inboundKey) => {
            if ((!newKeys.includes(inboundKey) || keysToDelete.includes(inboundKey)) && !alwaysRetainKeys.has(inboundKey)) {
                delete obj.INBOUND[inboundKey];
            }
        });
    
        // Add new properties based on adapter type
        if (adapterTypeAdditionalProperties[value]) {
            Object.assign(obj.INBOUND, adapterTypeAdditionalProperties[value]);
        }
      }

      else if (
        adapterBasedKeysMap.hasOwnProperty(ADAPTER_TYPE) &&
        adapterBasedKeysMap[ADAPTER_TYPE].includes(key)
      ) {
        // Update keys specific to adapter-based configurations in the "INBOUND" object.
        const adaptersRequiringBothLevels = ['HTTP', 'BridgeGateWebService', 'BridgeGate Web Service', 'GraphQL'];
        const shouldUpdateBothLevels = adaptersRequiringBothLevels.includes(ADAPTER_TYPE);
        if(value === null && obj?.INBOUND?.hasOwnProperty(key)){
           delete obj.INBOUND[key]
        } else if(shouldUpdateBothLevels && key === 'SYNCHRONOUS'){
          obj.INBOUND[key] = value;
          obj[key] = value;
        } else {
          obj.INBOUND[key] = value;
        }
      } 
      else if (bulk) {
        const adaptersRequiringBothLevels = ['HTTP', 'BridgeGateWebService', 'BridgeGate Web Service', 'GraphQL'];
        const shouldUpdateBothLevels = adaptersRequiringBothLevels.includes(bulk?.ADAPTER_TYPE || ADAPTER_TYPE);
       
        for (const property in bulk) {
          const value = bulk[property];
          
          // Set property values
          if (shouldUpdateBothLevels && property === 'SYNCHRONOUS') {
            obj.INBOUND[property] = value;
            obj[property] = value;
          } else if(property === 'connDetailUpdateFlag') {
            obj[property] = value;
          } else if(['ENABLED', 'BAM_CONN_MON_ENABLED'].includes(property)) {
            obj[property] = value;
          } else {
            obj.INBOUND[property] = value;
          }
          
          // Clean up null values
          if (value === null) {
            if (obj?.INBOUND?.hasOwnProperty(property)) {
              delete obj.INBOUND[property];
            }
            if (property !== "NAME" && obj?.hasOwnProperty(property)) {
              delete obj[property];
            }
          }
        }
      }
      else if (removeValues) {
        for(let prop in removeValues){
          if(obj?.INBOUND?.hasOwnProperty(prop)) delete obj.INBOUND[prop]
        }
      }
      else {
        // Update other properties directly on the object.
        obj[key] = value;
        if(value === null && obj?.hasOwnProperty(key)) delete obj[key];
      }

      // Dispatch an action to update the selected node in the state.
      dispatch(
        treeActions.setRightTreeSelectedNode({
          screen: parentScreen,
          type: rightTree.currentTab,
          data: obj,
        })
      );

      // If the object has children, return them for further iteration.
      if (isArray(obj?.children)) {
        return obj.children;
      }
    });

    // Dispatch an action to update the translated tree data in the state.
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedData,
      })
    );

    /**
     * SWT parity (GetDataScreen.resetService()):
     * Only remove SERVICE when changing the FIRST GetData adapter type to a non-service-supported adapter
     * AND the workflow no longer has any service-supported GetData anywhere.
     */
    if (
      key === "ADAPTER_TYPE" &&
      isSelectedFirstGetData &&
      templateHeader?.hasOwnProperty("SERVICE") &&
      !SERVICE_SUPPORTED_GETDATA_ADAPTER_TYPES.includes(value) &&
      !hasAnyServiceSupportedGetData(updatedData)
    ) {
      shouldDeleteServiceHeader = true;
    }

    if (shouldDeleteServiceHeader) {
      deleteServiceFromTemplateHeader();
    }
  };

  const debounceChangeHandler = useCallback(debounce(submitChange, 100), [rightTree.tree])

  // Handle changes to the data name, specific logic for onChange event
  const handleDataNameChange = (e) => {
    if (e?.target?.value || e?.target?.value === '') {
      setName(e.target.value);
    }
  }

  // Handle changes to the data name, specific logic for onBlur event
  const handleDataNameBlur = (eVal, e) => {
    const oldName = selectedNode?.NAME || '';
    
    const validationResult = validateWorkflowItemName(
      eVal, 
      oldName, 
      translatedData, 
      selectedNode?.key, 
      'GET_DATA'
    );
    
    if (validationResult.isValid) {
      const newName = eVal?.trim() || '';
      setName(newName);
      if (newName !== oldName) {
        // Mark template as changed immediately to ensure flag is set before tab switch
        dispatch(treeActions.setTemplateChanged({ screen: parentScreen, data: true }));
        submitChange('NAME', newName);
        // Wait for debounce delay + buffer before dispatching
        setTimeout(() => {
          dispatch(treeActions.setIsInputFocusedFalse());
        }, 200);
      } else {
        // If no change, dispatch immediately
        setTimeout(() => {
          dispatch(treeActions.setIsInputFocusedFalse());
        }, 100);
      }
    } else {
      if (validationResult.errorMessage) {
        setErrorMessage(t(validationResult.errorMessage));
        setErrorModal(true);
      } else if (validationResult.shouldRevert) {
        setName(oldName);
      }
      // For error cases, dispatch immediately
      setTimeout(() => {
        dispatch(treeActions.setIsInputFocusedFalse());
      }, 100);
    }
  }
  const handleRunOnServerChange = (e) => {
    setRunOnServer(e.target.value)
    debounceChangeHandler('RUN_ON_SERVER',e.target.value)
  }

  const handleErrorModal = (isOpen) => {
    setErrorModal(isOpen);
    if (!isOpen) {
      const oldName = selectedNode?.NAME || '';
      setName(oldName);
      setErrorMessage('');
    }
  }
  return (
    <Stack spacing='15px' className='tp-segment'>
      {/* Get Data Name */}
      <FormControl variant='outlined' className='width-100' size='small'>
        <InputLabel 
          htmlFor='get-data-name-input'
          sx={{padding: "0 6px !important", left: "-5px"}}
        >
          {t('COL_NAME')}
        </InputLabel>
        <OutlinedInput
          id="get-data-name-input"
          size='small'
          variant='outlined'
          fullWidth
          autoComplete='off'
          value={name}
          endAdornment={<div style={styles.getDataNameAdornment}>{`ID: ${wfItemId}`}</div>}
          sx={{justifyContent: "flex-end"}}
          onChange={(e) => handleDataNameChange(e)}
          onBlur={(e) => handleDataNameBlur(e.target.value, e)}
          onFocus={() => dispatch(treeActions.setIsInputFocusedTrue())}
        />
      </FormControl>
      {/* Checkboxes */}
      <GetDataCheckbox selectedNode={selectedNode} updateData={debounceChangeHandler} translatedData={translatedData} /> 
      {/* Run On Server */}
      {['FILE'].includes(selectedNode?.INBOUND?.ADAPTER_TYPE) && templateHeader && templateHeader?.hasOwnProperty('SERVICE') && templateHeader?.SERVICE.NAME === selectedNode?.title && templateHeader?.SERVICE?.ENABLED === "TRUE"  && (
        <FormControl>
          <InputLabel id='run-on-server'>{t('LBL_RUN_ON_SERVER')}</InputLabel>
          <Select 
           size='small'
           id='run-on-server'
           value={runOnServer}
           label={t('LBL_RUN_ON_SERVER')}
           onChange={handleRunOnServerChange}
          >
          {runOnServerOptions}
          </Select>
        </FormControl>
      )}
      {/* Encoding  */}
      <FormControl>
        <InputLabel id="encoding-dropdown">{t('ENCODING')}</InputLabel>
        <Select
          size='small'
          id='encoding-dropdown'
          value={encoding}
          label={t('ENCODING')}
          onChange={(e) => {
            setEncoding(e.target.value);
            debounceChangeHandler('ENCODING', e.target.value);
           }
          }
        >
          {encodingOptions}
        </Select>
      </FormControl>
      {/* Connection details */}
      { Object.keys(connectionInfo).length > 0 && <Accordion sx={styles.accordianContainer} defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="connection-info"
          id="connection-info"
          className="accordian-override"
          sx={styles.accordianHeader}
        >
          <SectionHeader text='Connection Details' />
        </AccordionSummary>
        <AccordionDetails sx={styles.accordianContainer}>
           <WorkflowConnectionPanel screen='GET_DATA' connectionDetails={connectionInfo} handleConnectionDetailsChange={handleConnectionDetailsChange} />
        </AccordionDetails>
      </Accordion>}
      
      {/* Error Modal */}
      <ErrorModal
        errorModal={errorModal}
        setErrorModal={setErrorModal}
        errorMessage={errorMessage}
        errorType={t('BOX_ERR')}
        handleErrorModal={handleErrorModal}
      />
    </Stack>
     
    
  )
}

export default GetDataProperties

/**
 * A map of adapter types to their corresponding configuration keys.
 * This object is used to determine which keys should be updated in the "INBOUND" object
 * based on the adapter type.
 *
 * Example:
 * - `BridgeGateSocket` includes configuration options like `PORT`, `EOD_TERMINATOR`, and others
 *   that are specific to this adapter.
**/
const adapterBasedKeysMap = {
  BridgeGateSocket: [
    'PORT',            
    'EOD_TERMINATOR',  
    'TIMEOUT',       
    'KEEP_ALIVE',     
    'USE_SINGLE_CONN', 
    'SSL',
    // 'OVERRIDE',
    'PREDEFINED' ,
    'ENCODING',
    'USE_WSDL',
    'ADAPTER_TYPE',
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY",
    // 'SYNCHRONOUS'    // Has to be updated directly on the selected node, not in the "INBOUND" object.
  ],
  DATABASE: [
    'ADAPTER_TYPE',
    'DATABASE_TYPE',            
    'JDBCURL',            
    'USERNAME',  
    'PASSWORD',
    'ENCODING',
  ],
  FILE: [
    "ADAPTER_TYPE",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY",
    "PREDEFINED",
    "FILE_LOCATION",
    "FILENAMEMASK",
    "DO_NOT_PROCESS_DUPLICATE_FILES",
    "ENABLE_DISTRIBUTED_FILE_PROC",
    "SPECIFY_DECOMPRESSION_DIRECTORY",
    "DECOMPRESSION_DIRECTORY",
    "USE_ZIP_PASSWORD",
    "ZIP_PASSWORD",
    "ZIP",
    "FILETIME_FILTER",
    "FILETIME_FILTER_MAX",
    "SORT_BY",
    "SORT_ORDER",
    "FILE_FILTER_PLUGIN",
    "ENCODING",
    "DELETE"
  ],
  BridgeGateHTTP: [
    "ADAPTER_TYPE",
    "AUTHENTICATE",
    "BTN_SEND_SYNC_RESPONSE",
    "DONT_RETAIN_PLAIN_TEXT",
    "DO_NOT_PROCESS_DUPLICATE_FILES",
    "ENCODING",
    "ENCRYPTION_PARAM1",
    "ENCRYPTION_TYPE",
    "REC_POST_DATA",
    "USE_WSDL",
    "USE_ZIP_PASSWORD",
    "ZIP",
    // Basic Auth keys
    "USERNAME",
    "PASSWORD",
    // Bearer Token key
    "TOKEN_NAME",
    // OAuth 2.0 keys
    "OAUTH2_TOKEN",
    "OAUTH2_HEADER_PREFIX",
    "OAUTH2_AUTO_REFRESH_TOKEN",
    "OAUTH2_ADD_TO",
    "OAUTH2_GRANT_TYPE",
    "OAUTH2_ACCESS_TOKEN_URL",
    "OAUTH2_CLIENT_ID",
    "OAUTH2_CLIENT_SECRET",
    "OAUTH2_USERNAME",
    "OAUTH2_PASSWORD",
    "OAUTH2_CLIENT_AUTHENTICATION",
    "OAUTH2_REFRESH_TOKEN_URL",
  ],
  BridgeGateFTP: [
    "ADAPTER_TYPE",
    "ENCODING",
    "PREDEFINED",
    "DELETE",
    "SSH",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY",
    "FILE_LOCATION",
    "FILENAMEMASK",
    "ACCOUNT",
    "TRADING_PARTNER_NAME",
    "SPECIFY_DECOMPRESSION_DIRECTORY",
    "DECOMPRESSION_DIRECTORY",
    "USE_ZIP_PASSWORD",
    "ZIP_PASSWORD",
    "ZIP",
  ],
  SSH: [
    "ADAPTER_TYPE",
    "CONNECTIONSTRING",
    "USERNAME",
    "PASSWORD",
    "SSH_SCRIPT",
    "ENCODING",
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "KEY",
    "KEY_PASSWORD",
  ],
  "Data Stream": [
    "ADAPTER_TYPE",
    "DO_NOT_PROCESS_DUPLICATE_FILES",
    "TIME_BEFORE_RETRY",
    "FILETIME_FILTER",
    "FILETIME_FILTER_MAX",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "RETRY_COUNT",
    "ENCRYPTION_PARAM1",
    "ENCODING",
  ],
  "Synchronous Response": [
    "TIMEOUT",
    "EOD_TERMINATOR",
    "ADAPTER_TYPE",
    "DO_NOT_PROCESS_DUPLICATE_FILES",
    "FILETIME_FILTER",
    "FILETIME_FILTER_MAX",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "ENCODING",
  ],
  AS2: [
    "ADAPTER_TYPE",
    "ENCODING",
    "TIME_BEFORE_RETRY",
    "RETRY_COUNT",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "AS2_COMPRESSION",
    "AS2_ENC_ALG",
    "AS2_ID",
    "AS2_MDN_SECURITY",
    "AS2_MDN_TRANSPORT",
    "AS2_MDN_URL",
    "AS2_SECURITY",
    "AS2_SIG_ALG",
    "AS2_TRANSPORT",
    "AS2_URL",
    "EMAIL",
    "SENDER_AS2_ID",
    "X509_ALIAS_ENCRYPTION",
    "X509_ALIAS_HTTPS",
    "X509_ALIAS_SIGNING",
    "NAME"
  ],
  "Web Service": [
    "ADAPTER_TYPE",
    "USE_WSDL",
    "WSDL_PATH",
    "WSDL_BINDING",
    "WSDL_BINDING_OPERATION",
    "WSDL_BINDING_SERVICE",
    "CONNECTIONSTRING",
    "TARGET_NAMESPACE",
    "REQUEST_ELEMENT_NAME",
    "SOAP_ACTION",
    "SOAP_PREFIX",
    "AUTHENTICATE",
    "USERNAME",
    "PASSWORD",
    "REALM",
    "TIMEOUT",
    "KEY_ALIAS",
    "SSL_TYPE",
    "WRITE_SOAP_BODY",
    "MAINTAIN_SESSION",
    "HTTP_CUSTOM_HEADER_FIELDS",
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
  ],
  "BridgeGateWebService": [
    "ADAPTER_TYPE",
    "CONNECTIONSTRING",
    "SERVICE_NAME",
    "TARGET_NAMESPACE",
    "AUTHENTICATE",
    "USERNAME",
    "PASSWORD",
    "SOAP_USE_ENVELOPE_AS_RESULT",
    "WRITE_SOAP_BODY",
    "SEND_SOURCE_AS_SOAP_BODY",
    "SYNCHRONOUS",
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "OPERATIONS"
  ], 
  EMAIL: [
    "ADAPTER_TYPE",
    "ENCODING",
    "TIME_BEFORE_RETRY",
    "RETRY_COUNT",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "CONNECTIONSTRING",
    "SSL",
    "PORT",
    "TLS",
    "USERNAME",
    "AUTH_TYPE",
    "PASSWORD",
    "TENANT_ID",
    "CLIENT_ID",
    "CLIENT_SECRET",
    "MAIL_TYPE",
    "GET_EMAIL_TYPE",
    "AFTER_READ_ACTION",
    "READ_FROM_ATTACHMENT",
    "DECOMPRESSION_DIRECTORY",
    "SPECIFY_DECOMPRESSION_DIRECTORY",
    "USE_ZIP_PASSWORD",
    "ZIP_PASSWORD",
    "ZIP",
    "REFRESH_TOKEN",
    "CREDENTIALS_JSON"
  ],
  SOCKET: [
    "CONNECTIONSTRING",
    "PORT",
    "KEEP_ALIVE",
    "TIMEOUT",
    "EOD_TERMINATOR",
    "ADAPTER_TYPE",
    "ENCODING",
    "TIME_BEFORE_RETRY",
    "RETRY_COUNT",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "PREDEFINED",
    "SSL",
    "KEY_ALIAS"
  ],
  FTP: [
    "PREDEFINED",
    "ADAPTER_TYPE",
    "ENCODING",
    "TIME_BEFORE_RETRY",
    "RETRY_COUNT",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "CONNECTIONSTRING",
    "ACCOUNT",
    "FILENAMEMASK",
    "FILE_LOCATION",
    "PASSWORD",
    "USERNAME",
    "DECOMPRESSION_DIRECTORY",
    "USE_ZIP_PASSWORD",
    "ZIP_PASSWORD",
    "ZIP",
    "BATCH_DOWNLOAD_LOC",
    "BATCH_MODE",
    "DELETE",
    "ASCII",
    "CACHE_CONNECTION",
    "PASSIVE_MODE",
    "DO_NOT_PROCESS_DUPLICATE_FILES",
    "POST_FTP_SCRIPT",
    "PRE_FTP_SCRIPT",
    "KEY",
    "KEY_PASSWORD",
    "SSL",
    "SSL_STRATEGY",
    "SSH",
  ],
  "Command Prompt": [
    "CMD",
    "EXECUTABLE",
    "CMD_ARGS",
    "FILENAME",
    "STREAM_ARGS",
    "STREAM_DATA",
    "ZIP",
    "COMPRESSION",
    "COMPRESSION_TYPE",
    "FILE_NAME_WITHIN_ZIP",
    "SPECIFY_DECOMPRESSION_DIRECTORY",
    "DECOMPRESSION_DIRECTORY",
    "USE_ZIP_PASSWORD",
    "ZIP_PASSWORD",
    "SCRIPT",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY",
    "ENCODING",
    // "OVERRIDE"
  ],
  AmazonSQS: [
    "ADAPTER_TYPE",
    "URL",
    "ACCESS_KEY_ID",
    "SECRET_KEY_ID",
    "TIMEOUT",
    "DURABLE_SUBSCRIPTION_CLIENT_ID",
    "MESSAGE_GROUP_ID",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY",
    "ENCODING",
  ],
  Kafka: [
    "PREDEFINED",
    "ADAPTER_TYPE",
    "TOPIC_NAME",
    "BOOTSTRAP_SERVERS_CONFIG",
    "GROUP_ID_CONFIG",
    "KEY_DESERIALIZER_CLASS_CONFIG",
    "VALUE_DESERIALIZER_CLASS_CONFIG", 
    "ENABLE_AUTO_COMMIT_CONFIG",
    "AUTO_OFFSET_RESET_CONFIG",
    "MAX_POLL_RECORDS_CONFIG",
    "TIMEOUT",
    "AUTH_TYPE",
    "USERNAME",
    "PASSWORD",
    "SSL_TYPE",
    "KEYSTORE",
    "KEYSTORE_PWD",
    "KEY_PWD",
    "TRUSTSTORE",
    "TRUSTSTORE_PWD",
    "DISABLE_QOS",
    "QUALITY_OF_SERVICE_1",
    "QUALITY_OF_SERVICE_2",
    "QUALITY_OF_SERVICE_3",
    "QUALITY_OF_SERVICE_4",
    "DON_T_USE_ENCRYPTION",
    "USE_ENCRYPTION",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_KEY",
    "COMPRESSION",
    "COMPRESSION_TYPE",
    "FILE_NAME_WITHIN_ZIP",
    "SPECIFY_DECOMPRESSION_DIRECTORY",
    "DECOMPRESSION_DIRECTORY",
    "USE_ZIP_PASSWORD",
    "ZIP_PASSWORD",
    "SCRIPT",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY",
    "ENCODING",
    "AUTH_TYPE",
  ],
  
  HTTP: [
    "AUTHENTICATE",
    "ADAPTER_TYPE",
    "CONNECTIONSTRING",
    "USERNAME",
    "PASSWORD",
    "KEY_ALIAS",
    "HTTP_TIMEOUT",
    "URLENCODE",
    "FORCE_HOST_VERIFICATION",
    "SYNCHRONOUS",
    "SSL_TYPE",
    "REQUEST_TYPE",
    "CONTENT_TYPE",
    "SOAP_ACTION",
    "ZIP",
    "FILE_NAME_WITHIN_ZIP",
    "ZIP_PASSWORD",
    "USE_ZIP_PASSWORD",
    "DECOMPRESSION_DIRECTORY",
    "SPECIFY_DECOMPRESSION_DIRECTORY",
    "HTTP_CUSTOM_HEADER_FIELDS",
    "ENCODING",
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "QOS_HTTP_CODES",
    // Bearer Token key
    "TOKEN_NAME",
    // OAuth 2.0 keys
    "OAUTH2_TOKEN",
    "OAUTH2_HEADER_PREFIX",
    "OAUTH2_AUTO_REFRESH_TOKEN",
    "OAUTH2_ADD_TO",
    "OAUTH2_GRANT_TYPE",
    "OAUTH2_ACCESS_TOKEN_URL",
    "OAUTH2_CLIENT_ID",
    "OAUTH2_CLIENT_SECRET",
    "OAUTH2_USERNAME",
    "OAUTH2_PASSWORD",
    "OAUTH2_CLIENT_AUTHENTICATION",
    "OAUTH2_REFRESH_TOKEN_URL",
    "HTTP_QUERY_PARAMETERS",
  ],
  ActiveMQ: [
    "ADAPTER_TYPE",
    "PREDEFINED",
    "TIMEOUT",
    "INIT_URL",
    "DESTINATION_TYPE",
    "QUEUE_OR_TOPIC_NAME",
    "DURABLE_SUBSCRIPTION_CLIENT_ID",
    "ENCODING",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY"
  ],
  "MQ Series": [
    "ADAPTER_TYPE",
    "CONNECTION_FACTORY_LOOKUP",
    "DESTINATION_TYPE",
    "QUEUE_OR_TOPIC_NAME",
    "DURABLE_SUBSCRIPTION_CLIENT_ID",
    "HOST_NAME",
    "PORT",
    "USERNAME",
    "PASSWORD",
    "CHANNEL",
    "TIMEOUT",
    "TRANSACTIONAL",
    "SSL",
    "CIPHER",
    "RUN_ON_PRIMARY",
    "ENCODING",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY"
  ],
  "BridgeGateMQSeries": [
    "ADAPTER_TYPE",
    "CONNECTION_FACTORY_LOOKUP",
    "DESTINATION_TYPE",
    "QUEUE_OR_TOPIC_NAME",
    "DURABLE_SUBSCRIPTION_CLIENT_ID",
    "HOST_NAME",
    "PORT",
    "USERNAME",
    "PASSWORD",
    "CHANNEL",
    "TIMEOUT",
    "TRANSACTIONAL",
    "SSL",
    "CIPHER",
    "RUN_ON_PRIMARY",
    "ENCODING",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY"
  ],
  Azure: [
    "ADAPTER_TYPE",
    "PREDEFINED",
    "AZURE_ACCOUNT_NAME",
    "AZURE_ACCOUNT_KEY",
    "AZURE_FILESYSTEM",
    "CREATE_FILESYS",
    "AZURE_DIRECTORY",
    "CREATE_DIRS",
    "AZURE_FILENAME",
    "DELETE",
    "APPEND_FILE",
    "ENCODING",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY"  
  ],
  MSMQ: [
    "ADAPTER_TYPE",
    "PREDEFINED",
    "PRIVATE_PUBLIC",
    "QUEUE_NAME",
    "URL",
    "DOMAIN",
    "USERNAME",
    "PASSWORD",
    "useNTLMv1",
    "NTLMv2",
    "DEADLETTER",
    "TRANSACTIONAL",
    "MSMQ_AUTOCOMMIT",
    "ENCODING",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY",
    "ALERT",
    "GUARANTY_DELIVERY",
    "ERROR_HANDLER_ID",    
  ],
  BridgeGateMSMQ: [
    "ADAPTER_TYPE",
    "PREDEFINED",
    "PRIVATE_PUBLIC",
    "QUEUE_NAME",
    "URL",
    "DOMAIN",
    "USERNAME",
    "PASSWORD",
    "useNTLMv1",
    "NTLMv2",
    "DEADLETTER",
    "TRANSACTIONAL",
    "MSMQ_AUTOCOMMIT",
    "ENCODING",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY",
    "ALERT",
    "GUARANTY_DELIVERY",
    "ERROR_HANDLER_ID",
  ],
  GraphQL: [
    "ADAPTER_TYPE",
    "CONNECTIONSTRING",
    "USERNAME",
    "PASSWORD",
    "KEY_ALIAS",
    "TIMEOUT",
    "URLENCODE",
    "FORCE_HOST_VERIFICATION",
    "SYNCHRONOUS",
    "SSL_TYPE",
    "REQUEST_TYPE",
    "CONTENT_TYPE",
    "SOAP_ACTION",
    "ZIP",
    "FILE_NAME_WITHIN_ZIP",
    "ZIP_PASSWORD",
    "USE_ZIP_PASSWORD",
    "DECOMPRESSION_DIRECTORY",
    "SPECIFY_DECOMPRESSION_DIRECTORY",
    "HTTP_CUSTOM_HEADER_FIELDS",
    "ENCODING",
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "QOS_HTTP_CODES"
  ],
  "Google Cloud Storage": [
    "ADAPTER_TYPE",
    "PREDEFINED",
    "OAUTH_REDIRECT_URL",
    "CLIENT_ID",
    "CLIENT_SECRET",
    "PROJECT_ID",
    "CREDENTIALS_JSON",
    "BUCKET_NAME",
    "OBJECT_NAME",
    "RETURN_DATA",
    "DELETE",
    "ENCODING",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY",
    "ZIP",
    "COMPRESSION",
    "COMPRESSION_TYPE",
    "FILE_NAME_WITHIN_ZIP",
    "USE_ZIP_PASSWORD",
    "ZIP_PASSWORD",
    "SPECIFY_DECOMPRESSION_DIRECTORY",
    "DECOMPRESSION_DIRECTORY"
  ],
  JMS: [
    "CONNECTION_FACTORY_LOOKUP",
    "CTX_FACTORY",
    "INIT_URL",
    "DESTINATION_TYPE",
    "QUEUE_OR_TOPIC_NAME",
    "DURABLE_SUBSCRIPTION_CLIENT_ID",
    "USERNAME",
    "PASSWORD",
    "ADAPTER_TYPE",
    "ENCODING",
    "PREDEFINED",
    "DONT_RETAIN_PLAIN_TEXT",
    "ENCRYPTION_TYPE",
    "ENCRYPTION_PARAM1",
    "RETRY_COUNT",
    "TIME_BEFORE_RETRY",

  ]
  // Add other Adapter Type configurations here as needed.
};

// Additional properties to be added based on the adapter type. used in the submitChange function when the ADAPTER_TYPE value is changed
const adapterTypeAdditionalProperties = {
  BridgeGateFTP: {
    USE_WSDL: "FALSE",
  },
  BridgeGateHTTP: {
    AUTHENTICATE: "TRUE",
    REC_POST_DATA: "FALSE",
    USE_WSDL: "FALSE",
  },
  EMAIL: {
    AUTH_TYPE: "BASIC_AUTH",
    MAIL_TYPE: "POP3",
    GET_EMAIL_TYPE: "ALL",
    AFTER_READ_ACTION: "DELETED",
    READ_FROM_ATTACHMENT: "FROM_EMAIL",
    SSL_TYPE: "TLSv1",
  },
  SOCKET: {
    USE_WSDL: "FALSE",
  },
  "Web Service": {
    USE_WSDL: "FALSE",
    MAINTAIN_SESSION: "FALSE",
    TIMEOUT: "30",
    SSL_TYPE: "TLSv1",
  },
  FTP: {
    ASCII: "FALSE",
    CACHE_CONNECTION: "TRUE",
    PASSIVE_MODE: "FALSE",
    USE_WSDL: "FALSE",
    BATCH_MODE: "FALSE"
  },
  SSH: {
    
  },
  "Command Prompt": {
  },
  AmazonSQS: {

  },
  KAFKA: {
    
  },
  HTTP: {
    URLENCODE: "TRUE",
    SSL_TYPE: "TLSv1.2",
  },
  ActiveMQ: {
    DESTINATION_TYPE: "QUEUE"
  },
  "MQ Series": {
    DESTINATION_TYPE: "QUEUE",
    CHANNEL: "SYSTEM.DEF.SVRCONN",
    ENCODING : "UTF-8",
    USE_WSDL : "FALSE"
  },
  "BridgeGateMQSeries": {
    DESTINATION_TYPE: "QUEUE",
    CHANNEL: "SYSTEM.DEF.SVRCONN",
    ENCODING : "UTF-8",
    USE_WSDL : "FALSE"
  },
  Azure: {
    APPEND_FILE: "FALSE",
    DO_NOT_PROCESS_DUPLICATE_FILES: "FALSE",
    USE_WSDL: "FALSE"
  },
  MSMQ: {
    PRIVATE_PUBLIC: "PRIVATE",
    DEADLETTER: "FALSE",
    TRANSACTIONAL: "FALSE",
    MSMQ_AUTOCOMMIT: "TRUE",
  },
  JMS: {
    DESTINATION_TYPE: "TOPIC"
  }
};

const styles = {
  accordianContainer: {
    border: "none",
    padding: "0",
    margin: "0",
    boxShadow: "none",
    backgroundColor: "unset",
  },
  accordianHeader: {
    border: "none",
    padding: "0",
    margin: "0",
    backgroundColor: "unset",
  },
  accordianContent: {
    border: "none",
    padding: "0 15px",
    margin: "0",
    backgroundColor: "unset",
  },
  getDataNameAdornment: {
    color: "#666666",
    fontSize: "9px",
    position: "absolute",
    right: "13px",
    top: "-6px",
    backgroundColor: "#FFF",
    zIndex: "2",
    padding: "0 5px",
    height: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }
};

export { adapterBasedKeysMap };
